// One SES account on the warmup board (2026-10-23): the owner's daily number,
// today's spend against it, how it is split between the account's pools and,
// inside a pool several countries share, each country's own part and today's
// spend against it (2026-10-25), the counts the split rests on, a plain
// sentence when the number reaches nobody, and the moves — start, a new
// number, stop, release —
// each with a reason. These are the page's primary controls; the account's
// decision and log (`AccountHistory`) are in "Részletek".

import { useId, useState } from "react";
import { toast } from "sonner";
import { GaugeIcon, PauseIcon, PlayIcon, RotateCcwIcon } from "lucide-react";
import {
  pauseWarmupAccount,
  releaseWarmupAccount,
  setWarmupAccountLimit,
  startWarmupAccount,
} from "@/lib/api";
import type { WarmupAccount, WarmupAccountCountry } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardAction, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ActionDialog } from "@/components/emc/ActionDialog";
import { LimitBar } from "@/components/emc/charts/LimitBar";
import { Country } from "@/components/emc/Flag";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout } from "@/components/emc/states";
import { count } from "@/components/emc/format";
import { cn } from "@/lib/utils";
import { useIsAdmin } from "@/pages/warmup/actions";
import { accountEventWords, accountNumberOf } from "@/pages/warmup/model";
import { reasonText } from "@/pages/warmup/reasons";
import { accountNotice } from "@/pages/warmup/simple";
import type { CountryRow } from "@/pages/warmup/simple";

// The API's note limit (`WarmupNoteIn`).
const NOTE_MAX = 300;

type Move = "start" | "limit" | "pause" | "release";

interface Props {
  account: WarmupAccount;
  onChanged: () => void;
}

export function AccountCard({
  account,
  countries,
  onChanged,
}: Props & { countries: CountryRow[] }) {
  const stopped = account.pools.filter((pool) => pool.is_paused || pool.held_by_watch);
  const cold = account.state === "cold";
  const notice = accountNotice(account, countries);
  const closed = new Set(countries.filter((row) => row.status === "closed").map((row) => row.country));
  const onCampaigns = account.basis === "campaigns";

  return (
    <Card
      id={`account-${account.code}`}
      data-account={account.code}
      className={cn("scroll-mt-20 gap-4", stopped.length > 0 && "ring-1 ring-destructive/45")}
    >
      <CardHeader className="has-data-[slot=card-action]:grid-cols-[auto_1fr_auto] gap-x-3">
        <span
          aria-hidden="true"
          className="row-span-2 flex size-9 items-center justify-center self-center rounded-control bg-fill-secondary text-label font-title"
        >
          {account.code}
        </span>
        <CardTitle>SES-fiók {account.code}</CardTitle>
        <CardAction className="col-start-3 flex-wrap justify-end">
          <StatusPill domain="warmup" state={account.state} />
        </CardAction>
        <CardDescription>
          {account.pools.map((pool) => pool.code).join(" · ") || "nincs pool"} ·{" "}
          {account.ramp ? "a rámpa tiszta nap után emel" : "a számot ember tartja, a rámpa nem emel"}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1">
          <span className="flex items-baseline gap-2">
            <span className="caps-label">Napi szám</span>
            <span className="text-title font-title tabular-nums">{count(account.daily_limit)}</span>
          </span>
          <span className="text-label text-muted-foreground tabular-nums">
            SES 24 órás kvóta: {count(account.ses_daily_quota)}
          </span>
        </div>
        {account.over_quota && (
          <Callout tone="warn">
            A szám ({count(account.daily_limit)}) a fiók SES-kvótája ({count(account.ses_daily_quota)}{" "}
            / 24 óra) fölött van: a kód engedi, de az AWS a kvóta fölött visszadobja a küldést.
          </Callout>
        )}

        {!cold && (
          <LimitBar used={account.sent_today} limit={account.daily_limit} label="Ma kiment / napi szám" />
        )}

        {notice !== null &&
          (notice.tone === "warn" ? (
            <Callout tone="warn">{notice.text}</Callout>
          ) : (
            <p className="text-muted-foreground">{notice.text}</p>
          ))}

        {stopped.length > 0 && (
          <Callout tone="warn">
            Áll: {stopped.map((pool) => pool.code).join(", ")}.{" "}
            {stopped
              .map((pool) => (pool.paused_reason ? `${pool.code}: ${reasonText(pool.paused_reason)}` : ""))
              .filter(Boolean)
              .join(" · ")}{" "}
            A része nem száll át a többi poolra; a feloldás onnan folytatja.
          </Callout>
        )}

        <div className="flex flex-col gap-1.5">
          <span className="caps-label">
            {onCampaigns
              ? `Felosztás · a mai kampányok hátralévő címzettjei arányában · ${count(account.recipients ?? 0)} címzett`
              : `Felosztás · a küldhető kontaktok arányában (ma nincs kampány) · ${count(account.mailable)} küldhető`}
          </span>
          {account.split.length === 0 ? (
            <p className="text-muted-foreground">
              Még nincs felosztás: indításkor számolja a kód, és újra, amint egy ország nyílik
              vagy zárul.
            </p>
          ) : (
            <table className="w-full text-body tabular-nums">
              <thead>
                <tr className="text-label text-muted-foreground">
                  <th className="text-left font-normal">
                    {onCampaigns ? "Ország · hátralévő címzett" : "Ország · küldhető"}
                  </th>
                  <th className="text-right font-normal">Rész / nap</th>
                  <th className="text-right font-normal">Ma kiment</th>
                </tr>
              </thead>
              {account.split.map((share) => {
                const live = account.pools.find((pool) => pool.code === share.code);
                const shared = share.countries.length > 1;
                return (
                  <tbody key={share.code} data-pool-share={share.code}>
                    <tr className="border-t border-separator" title={`pool: ${share.code}`}>
                      <td className="py-1">
                        {share.countries.length === 0 && (
                          <span className="text-muted-foreground">{share.code}: nincs aktív ország</span>
                        )}
                        {shared && (
                          <span className="text-muted-foreground">
                            {share.code} · {share.countries.length} ország · {count(share.mailable)}
                          </span>
                        )}
                        {!shared &&
                          share.countries.map((country) => (
                            <CountryCell
                              key={country.country}
                              country={country}
                              closed={closed}
                              onCampaigns={onCampaigns}
                            />
                          ))}
                      </td>
                      <td className="py-1 text-right">{count(share.share)}</td>
                      <td className="py-1 text-right">{live ? count(live.sent_today) : "—"}</td>
                    </tr>
                    {shared &&
                      share.countries.map((country) => (
                        <tr key={country.country} data-country-share={country.country}>
                          <td className="py-0.5 pl-4">
                            <CountryCell country={country} closed={closed} onCampaigns={onCampaigns} />
                          </td>
                          <td className="py-0.5 text-right">
                            {country.share == null ? "—" : count(country.share)}
                          </td>
                          <td className="py-0.5 text-right">{count(country.sent_today ?? 0)}</td>
                        </tr>
                      ))}
                  </tbody>
                );
              })}
            </table>
          )}
          {account.split_at && (
            <p className="text-label text-muted-foreground">
              Számolva {new Date(account.split_at).toLocaleString("hu-HU")}. A szám a mai (ütemezett,
              futó vagy szüneteltetett) kampányok hátralévő címzettjei arányában oszlik; kampány nélküli
              ország nem kap részt. Újraszámol, ha egy kampány elindul, megáll, folytatódik, véget ér
              vagy törlődik. Napközben is a szám arányos részét adja, a már elküldöttnél soha nem
              kevesebbet: a nap összesen sosem több a számnál, és aki korábban kezd, nem viszi el a
              többiek részét; ami egy országnak megmarad, aznap nem száll át a többire.
            </p>
          )}
        </div>
      </CardContent>

      <CardFooter className="justify-end gap-3">
        <AccountActions account={account} onChanged={onChanged} />
      </CardFooter>
    </Card>
  );
}

/** A country of the split: its flag and the count its part rests on — the
 * recipients today's campaigns still have to send, or its mailable contacts. */
function CountryCell({
  country,
  closed,
  onCampaigns,
}: {
  country: WarmupAccountCountry;
  closed: Set<string>;
  onCampaigns: boolean;
}) {
  return (
    <span className="inline-flex items-baseline gap-1">
      <Country code={country.country} />
      <span className="text-muted-foreground">
        {onCampaigns
          ? (country.recipients ?? 0) > 0
            ? count(country.recipients ?? 0)
            : "ma nincs kampány"
          : count(country.mailable)}
        {closed.has(country.country) ? " · zárva" : ""}
      </span>
    </span>
  );
}

/** The account's last decision and the number's log: in "Részletek". */
export function AccountHistory({ account }: { account: WarmupAccount }) {
  if (!account.decision && account.events.length === 0) return null;
  return (
    <div className="flex flex-col gap-3" data-account-history={account.code}>
      <span className="text-body font-semibold">SES-fiók {account.code}</span>
      {account.decision && (
        <div className="flex flex-col gap-1">
          <span className="caps-label">
            {account.decision.today ? "Mai döntés" : `Utolsó döntés · ${account.decision.day}`}
          </span>
          <p className="text-body">{reasonText(account.decision.reason)}</p>
          <p className="text-label text-muted-foreground">
            {account.decision.actor} · {new Date(account.decision.decided_at).toLocaleString("hu-HU")}
          </p>
        </div>
      )}

      {account.events.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="caps-label">A szám naplója</span>
          <ul className="flex flex-col gap-0.5 text-label">
            {account.events.map((event, index) => (
              <li key={`${event.occurred_at}-${index}`} className="text-muted-foreground">
                <span className="tabular-nums">{new Date(event.occurred_at).toLocaleString("hu-HU")}</span> ·{" "}
                {accountEventWords(event)} · {event.actor}
                {event.note ? ` — ${reasonText(event.note)}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AccountActions({ account, onChanged }: Props) {
  const isAdmin = useIsAdmin();
  const [open, setOpen] = useState<Move | null>(null);
  const [number, setNumber] = useState("");
  const [ramp, setRamp] = useState(false);
  const numberId = useId();
  const rampId = useId();
  if (!isAdmin) return null;
  const code = account.code;

  function done(message: string) {
    toast(message);
    onChanged();
  }

  function openWith(move: Move, defaultRamp: boolean) {
    setNumber(move === "limit" ? String(account.daily_limit) : "");
    setRamp(defaultRamp);
    setOpen(move);
  }

  const typed = Number(number.trim().replace(/\s/g, ""));
  const overQuota = Number.isFinite(typed) && typed > account.ses_daily_quota;

  const numberField = (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={numberId}>Napi szám a teljes fióknak</Label>
        <Input
          id={numberId}
          inputMode="numeric"
          value={number}
          placeholder="pl. 15000"
          onChange={(event) => setNumber(event.target.value)}
        />
        <p className="text-label text-muted-foreground">
          Azonnal él, és a kód a poolok közt osztja szét a kiküldhető kontaktok arányában; ma már
          kiment levél nem számít újra. SES-kvóta: {count(account.ses_daily_quota)} / 24 óra.
        </p>
        {overQuota && (
          <Callout tone="warn">
            Ez a fiók SES-kvótája fölött van. A kód engedi, de az AWS a kvóta fölött visszadobja.
          </Callout>
        )}
      </div>
      <label htmlFor={rampId} className="flex items-center gap-2 text-body">
        <Checkbox id={rampId} checked={ramp} onChange={(event) => setRamp(event.target.checked)} />
        A rámpa tiszta nap után tovább emelheti
      </label>
    </div>
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      {account.can_start && (
        <Button size="sm" type="button" onClick={() => openWith("start", true)}>
          <PlayIcon />
          Indítás
        </Button>
      )}
      {account.can_set_limit && (
        <Button size="sm" variant="outline" type="button" onClick={() => openWith("limit", false)}>
          <GaugeIcon />
          Napi szám
        </Button>
      )}
      {account.can_release && (
        <Button size="sm" type="button" onClick={() => setOpen("release")}>
          <RotateCcwIcon />
          Feloldás
        </Button>
      )}
      {account.can_pause && (
        <Button size="sm" variant="outline-danger" type="button" onClick={() => setOpen("pause")}>
          <PauseIcon />
          Megállítás
        </Button>
      )}

      <ActionDialog
        open={open === "start"}
        onOpenChange={(next) => setOpen(next ? "start" : null)}
        title={`A(z) ${code} SES-fiók indítása`}
        description={
          <>
            A fiók minden poolja ({account.pools.map((pool) => pool.code).join(", ")}) indul, a megadott
            napi számmal. A 4. szabály küszöbei fölött a pool vagy a fiók azonnal megáll.
          </>
        }
        confirmLabel="Fiók indítása"
        reason={{ label: "Miért indul most?", placeholder: "pl. első hullám", max: NOTE_MAX }}
        onConfirm={async (note) => {
          await startWarmupAccount(code, accountNumberOf(number, 1), note, ramp);
          done(`A(z) ${code} fiók elindult.`);
        }}
      >
        {numberField}
      </ActionDialog>

      <ActionDialog
        open={open === "limit"}
        onOpenChange={(next) => setOpen(next ? "limit" : null)}
        title={`A(z) ${code} SES-fiók napi száma`}
        description={
          <>
            Most {count(account.daily_limit)} / nap. Bármikor átírható, napon belül is, fel és le; a
            naplóba a neved, a régi és az új szám és az indoklás kerül. A megállított poolt nem
            indítja el — a feloldás külön lépés.
          </>
        }
        confirmLabel="Szám beállítása"
        reason={{ label: "Miért ennyi?", placeholder: "pl. jó volt az első kör", max: NOTE_MAX }}
        onConfirm={async (note) => {
          await setWarmupAccountLimit(code, accountNumberOf(number, 0), note, ramp);
          done(`A(z) ${code} fiók napi száma beállítva.`);
        }}
      >
        {numberField}
      </ActionDialog>

      <ActionDialog
        open={open === "pause"}
        onOpenChange={(next) => setOpen(next ? "pause" : null)}
        title={`A(z) ${code} SES-fiók megállítása`}
        description={
          <>
            A fiók minden poolja azonnal áll: a limiter a következő küldési kérésnél megtagadja, és
            kritikus riasztás megy ki. A szám marad ({count(account.daily_limit)}): a feloldás onnan
            folytatja.
          </>
        }
        confirmLabel="Fiók megállítása"
        tone="danger"
        reason={{ label: "Miért állítod meg?", placeholder: "pl. furcsa a Gmail-kézbesítés", max: NOTE_MAX }}
        onConfirm={async (note) => {
          await pauseWarmupAccount(code, note);
          done(`A(z) ${code} fiók megállt.`);
        }}
      />

      <ActionDialog
        open={open === "release"}
        onOpenChange={(next) => setOpen(next ? "release" : null)}
        title={`A(z) ${code} SES-fiók feloldása`}
        description={
          <>
            A fiók minden megállított poolja újra megy. Ha a mai számok még a küszöb fölött vannak, az
            ütemező a következő percben újra megállítja.
          </>
        }
        confirmLabel="Fiók feloldása"
        reason={{ label: "Miért engeded tovább?", placeholder: "pl. megnéztem, a jel elmúlt", max: NOTE_MAX }}
        onConfirm={async (note) => {
          await releaseWarmupAccount(code, note);
          done(`A(z) ${code} fiók újra megy.`);
        }}
      />
    </div>
  );
}
